import { createClient, RawQueryResponse } from 'next-sanity'

import {apiVersion, dataset, projectId, STUDIO_ORIGIN, token, useCdn} from '../env'
import {
  containsProfitSplitMarker,
  PROFIT_SPLIT_FALLBACK,
  PROFIT_SPLIT_QUERY,
  replaceProfitSplitMarker,
  resolveProfitSplit,
} from '@/lib/profitSplit'

export type QueryParams = {[key: string]: string | number | boolean | string[]}

const DEFAULT_PARAMS = {} as QueryParams
const DEFAULT_TAGS = [] as string[]

// Time-based revalidation is only a safety net: /api/revalidate now busts the 'sanity'
// tag on every publish, so content propagates on the webhook, not on this timer.
// Deliberately not Infinity — if a webhook delivery is ever missed (Sanity outage,
// rotated secret, webhook disabled), this is the only thing that recovers the site
// without a deploy.
const REVALIDATE_SECONDS = 86_400 // 24h

export const client = createClient({
  apiVersion,
  dataset,
  projectId,
  useCdn,
  stega: {
    enabled: true,
    studioUrl: STUDIO_ORIGIN,
  },
})

export const prodClient = createClient({
  apiVersion,
  dataset,
  projectId,
  useCdn,
  stega: {
    enabled: false,
    studioUrl: STUDIO_ORIGIN,
  },
})

export async function sanityFetch<QueryResponse>({
                                                   query,
                                                   params = DEFAULT_PARAMS,
                                                   tags = DEFAULT_TAGS,
                                                   isDraftMode = false,
                                                 }: {
  query: string
  params?: QueryParams
  tags?: string[],
  isDraftMode?: boolean,
}): Promise<RawQueryResponse<QueryResponse>> {

  if (isDraftMode && !token) {
    throw new Error('The `SANITY_API_READ_TOKEN` environment variable is required.')
  }

  // Every fetch carries the 'sanity' base tag. /api/revalidate busts it whenever a
  // shared document (header, footer, affiliatesTables, …) is published — those docs
  // are resolved inline inside page queries, so no tag of their own ever reaches
  // their cache entries. Without the base tag here, metadata, slug lookups and
  // redirect queries stay stuck for the full revalidate window.
  const allTags = ['sanity', ...tags];

  const response = await (isDraftMode ? client : prodClient).fetch<QueryResponse>(query, params, {
    ...(isDraftMode && {
      token: token,
      perspective: 'previewDrafts',
    }),
    next: {
      revalidate: isDraftMode ? 0 : process.env.NODE_ENV === 'development' ? 20 : REVALIDATE_SECONDS,
      tags: allTags,
    },
    filterResponse: false
  })

  return withProfitSplit(response, isDraftMode)
}

// Swaps the {profitSplit} placeholder on the way out of every fetch.
async function withProfitSplit<R extends RawQueryResponse<unknown>>(
  response: R,
  isDraftMode: boolean,
): Promise<R> {
  const result = response?.result
  if (!containsProfitSplitMarker(result)) return response

  // Page queries already carry the value (BasePageQuery).
  const split =
    result && typeof result === 'object' && 'profitSplit' in result
      ? resolveProfitSplit((result as { profitSplit?: unknown }).profitSplit)
      : await fetchProfitSplit(isDraftMode)

  return {
    ...response,
    result: replaceProfitSplitMarker(result, split ?? PROFIT_SPLIT_FALLBACK),
  }
}

async function fetchProfitSplit(isDraftMode: boolean): Promise<number | null> {
  const value = await (isDraftMode ? client : prodClient).fetch<unknown>(PROFIT_SPLIT_QUERY, {}, {
    ...(isDraftMode && {
      token: token,
      perspective: 'previewDrafts',
    }),
    next: {
      revalidate: isDraftMode ? 0 : process.env.NODE_ENV === 'development' ? 20 : REVALIDATE_SECONDS,
      tags: ['sanity', 'profitSplitSettings'],
    },
  })
  return resolveProfitSplit(value)
}

// Kept as a named alias — sanityFetch now applies the same base tag.
export const sanityFetchWithTags = sanityFetch
